import {Badge, IconButton, keyframes} from '@mui/material';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import {useState, useEffect, useImperativeHandle, forwardRef} from 'react';
import {Chat} from './Chat';
import {SidebarDialog} from '@ui/SidebarDialog/SidebarDialog';
import {useChatContext} from './context';
import {useTranslation} from 'react-i18next';
import {setChatRef} from './globalChatManager';
import {useCallContext} from './CallContext';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import {LightTooltip} from '@ui/LightTooltip/LightTooltip';
export interface ChatInHeaderRef {
    openChatWithUser: (username: string) => void;
    isChatOpen: () => boolean;
}
const greenBlink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: .35; transform: scale(1.2); }
`;
const redBlink = keyframes`
  0%   { transform: translate(50%, -50%) scale(1); }
  10%  { transform: translate(50%, -50%) scale(1.5); } 
  20%  { transform: translate(50%, -50%) scale(1);   }
  100% { transform: translate(50%, -50%) scale(1); }
`;

export const ChatInHeader = forwardRef<ChatInHeaderRef>((props, ref) => {
    const {t} = useTranslation(['header']);
    const [isOpen, setIsOpen] = useState(false);
    const {totalUnreadCount, getPrivateChat} = useChatContext();
    const {state, activePeer} = useCallContext();
    const isCallingOrInCall = state === 'calling' || state === 'in-call';

    const chatMethods = {
        openChatWithUser: (username: string) => {
            setIsOpen(true);
            getPrivateChat(username);
        },
        isChatOpen: () => isOpen,
    };

    useImperativeHandle(ref, () => chatMethods);

    useEffect(() => {
        setChatRef(chatMethods);
        return () => setChatRef(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const hasUnread = (totalUnreadCount ?? 0) > 0;

    return (
        <>
            <LightTooltip title={activePeer ? t('chat.callWith') + ' ' + activePeer : ''}>
                <IconButton onClick={() => setIsOpen(true)}>
                    <Badge
                        badgeContent={totalUnreadCount}
                        color='error'
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        slotProps={{
                            badge: hasUnread
                                ? {
                                      sx: {
                                          animation: `${redBlink} 5s ease-in-out infinite`,
                                          transformOrigin: '100% 0%',
                                          willChange: 'transform',
                                      },
                                  }
                                : undefined,
                        }}
                    >
                        {isCallingOrInCall ? (
                            <PhoneForwardedOutlinedIcon
                                fontSize='large'
                                sx={{
                                    color: 'success.main',
                                    animation: `${greenBlink} 1s ease-in-out infinite`,
                                    willChange: 'opacity, transform',
                                    filter: 'drop-shadow(0 0 4px rgba(46, 204, 113, .7))',
                                }}
                            />
                        ) : (
                            <QuestionAnswerOutlinedIcon fontSize='large' />
                        )}
                    </Badge>
                </IconButton>
            </LightTooltip>
            <SidebarDialog
                maxWidth='760px'
                title={t('chat.chat')}
                open={isOpen}
                onClose={() => setIsOpen(false)}
            >
                <Chat />
            </SidebarDialog>
        </>
    );
});
